import RequirementsProvider from "@rentiohq/shared-frontend/dist/providers/RequirementsProvider";
import { EDashboardType } from "@rentiohq/shared-frontend/dist/redux/types";
import { useDashboardType } from "@rentiohq/shared-frontend/dist/reduxV2/auth/auth.hooks";
import {
  IAlert,
  registerAlertTrigger,
} from "@rentiohq/shared-frontend/dist/utils/alert/alert.utils";
import { getMessageForError } from "@rentiohq/shared-frontend/dist/utils/error.utils";
import {
  ErrorBoundary,
  ModalProvider,
  PrivateRoute,
} from "@rentiohq/web-shared/dist/components";
import {
  toast,
  ToastContainer,
} from "@rentiohq/web-shared/dist/managers/Toast";
import { getAuthScene } from "@rentiohq/web-shared/dist/scenes/Auth/Auth.scene";
import { getTemplateDocumentEditorScene } from "@rentiohq/web-shared/dist/scenes/TemplateDocumentEditor";
import { TemplateDocumentExternal } from "@rentiohq/web-shared/dist/scenes/TemplateDocumentEditor/scenes/TemplateDocumentExternal";
import { getTemplatesScene } from "@rentiohq/web-shared/dist/scenes/Templates";
import { App } from "App";
import InvitesModal from "modals/InvitesModal";
import React from "react";
import {
  Outlet,
  Route,
  Routes,
  useLocation,
  useNavigate,
} from "react-router-dom";
import { OfficeAdminRoute } from "routes/OfficeAdminRoute";
import { getChatsScene } from "scenes/Chats";
import { getCompanyScene } from "scenes/Company";
import { getContactsScene } from "scenes/Contacts";
import { getDashboardScene } from "scenes/Dashboard";
import { getFollowUpScene } from "scenes/FollowUp";
import { getFormsScene } from "scenes/Forms";
import { NotFound } from "scenes/NotFound";
import { getPaymentsOverviewScene } from "scenes/PaymentsOverview";
import { getProfileScene } from "scenes/Profile";
import { getPropertiesScene } from "scenes/Properties";
import { VisitMode } from "scenes/Properties/scenes/VisitMode";
import { getRentioLegalScene } from "scenes/RentioLegal/RentioLegal.scene";
import { getRentmatchAcademyScene } from "scenes/RentmatchAcademy/RentmatchAcademy.scene";
import { getReportsScene } from "scenes/Reports";
import { getRevenuesScene } from "scenes/Revenues";
import { getSettingsScene } from "scenes/Settings";
import { getCostsScene } from "scenes/Settlements/Costs.scene";
import { getTasksScene } from "scenes/Tasks";
import { TenantDashboardApp } from "tenant-dashboard/App";
import { PrivateRoutes } from "tenant-dashboard/routes/PrivateRoutes";
import { RentioThemeProvider } from "utils/theme.provider";
import { ConfirmProvider } from "../providers/ConfirmProvider";
import { ConnectAuthorize } from "../scenes/Connect";
import { ExternalAction } from "../scenes/ExternalAction";
import { RentioInternal } from "../scenes/RentioInternal";
import { getContactsScene as getTenantContactsScene } from "../tenant-dashboard/scenes/Contacts/Contacts.scene";
import { getDashboardScene as getTenantDashboardScene } from "../tenant-dashboard/scenes/Dashboard/Dashboard.scene";
import { getPaymentsScene } from "../tenant-dashboard/scenes/Payments/Payments.scene";
import { getProfileScene as getTenantProfileScene } from "../tenant-dashboard/scenes/Profile/Profile.scene";
import { getPropertyScene } from "../tenant-dashboard/scenes/Property/Property.scene";
import { getTasksScene as getTenantTasksScene } from "../tenant-dashboard/scenes/Tasks/Tasks.scene";

const ProDashboardWrapper = () => {
  const location = useLocation();

  // Visit mode renders outside <App> and draws no top navigation, so a message
  // pushed clear of one landed a bar's height down an empty screen, under the
  // title rather than over it.
  const hasTopNavigation = !location.pathname.endsWith("/visit-mode");

  return (
    <RentioThemeProvider>
      <ErrorBoundary>
        <ToastContainer topOffset={hasTopNavigation ? undefined : 0} />
        <ModalProvider>
          <RequirementsProvider>
            <ConfirmProvider>
              <Outlet />
              <InvitesModal />
            </ConfirmProvider>
          </RequirementsProvider>
        </ModalProvider>
      </ErrorBoundary>
    </RentioThemeProvider>
  );
};

export const AppRouter = () => {
  const { selectedDashboardType } = useDashboardType();

  const selectedDashboardTypeMapped =
    selectedDashboardType === EDashboardType.TenantDashboard
      ? EDashboardType.TenantDashboard
      : EDashboardType.ProDashboard;

  const navigate = useNavigate();

  React.useEffect(() => {
    const alertTrigger = ({
      message = "",
      error,
      type,
      content,
      action,
    }: IAlert) => {
      toast({
        variation: type,
        heading: error ? getMessageForError(error) : message,
        content,
        action,
        navigate,
      });
    };
    registerAlertTrigger(alertTrigger, "main");
  }, [navigate]);

  return (
    <Routes>
      <Route path="*">
        <Route path="*" element={<ProDashboardWrapper />}>
          {getAuthScene()}

          <Route path="rentio-internal" element={<RentioInternal />} />

          {/* INFO: MCP connector consent. Public (outside PrivateRoute) so it can host its
              own sign-in redirect; the scene itself gates on the auth token. */}
          <Route path="connect/authorize" element={<ConnectAuthorize />} />

          {/* INFO: the page every external-action link in a mail opens. Public: the reader
              may never have signed in. It acts on its button only, never on opening
              (DEV-3330). */}
          <Route path="external-action" element={<ExternalAction />} />

          <Route
            path="template-document-external"
            element={<TemplateDocumentExternal />}
          />

          {selectedDashboardTypeMapped === EDashboardType.ProDashboard && (
            <Route element={<PrivateRoute />}>
              {/* Visit mode is signed in like the rest of the dashboard, but
                  renders outside <App>: it takes the whole screen on a phone
                  and the mail sent an hour before a viewing links straight to
                  it, so there is no navigation to draw around it (DEV-3227). */}
              <Route
                path="properties/:propertyId/visit-mode"
                element={<VisitMode />}
              />

              <Route path="*" element={<App />}>
                {getDashboardScene()}

                <Route
                  path="monitor"
                  element={
                    <div>Switched dashboard via monitor (API call saver)</div>
                  }
                />

                {getTasksScene()}

                {getFormsScene()}

                {getRevenuesScene()}

                {getChatsScene()}

                {getTemplateDocumentEditorScene()}

                {getFollowUpScene()}

                {getPaymentsOverviewScene()}

                {getContactsScene()}

                {getProfileScene()}

                <Route element={<OfficeAdminRoute />}>
                  {getCompanyScene()}

                  {getSettingsScene()}
                </Route>

                {getPropertiesScene()}

                {getReportsScene()}

                {getRentioLegalScene()}

                {getCostsScene()}

                {getRentmatchAcademyScene()}

                {getTemplatesScene({ internal: false })}

                <Route path="*" element={<NotFound />} />
              </Route>
            </Route>
          )}

          <Route path="*" element={<NotFound />} />
        </Route>

        {selectedDashboardTypeMapped === EDashboardType.TenantDashboard && (
          <Route path="*">
            <Route element={<PrivateRoute />}>
              <Route path="*" element={<TenantDashboardApp />}>
                {getPropertyScene()}

                {getTenantDashboardScene()}

                {getTenantContactsScene()}

                {getTenantTasksScene()}

                {getPaymentsScene()}
                {getTenantProfileScene()}

                {PrivateRoutes.map(route => (
                  <Route
                    key={route.path}
                    path={route.path}
                    element={route.element}
                  />
                ))}

                {/* 
                // TODO: This should be a new not found component */}
                <Route path="*" element={<>Page not found</>} />
              </Route>
            </Route>
          </Route>
        )}
      </Route>
    </Routes>
  );
};
